import { useState } from 'react';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import {
  useReadingThresholds,
  useSaveThresholds,
} from '@/features/home-readings/api';
import {
  THRESHOLD_FIELDS,
  draftOf,
  validateThresholds,
  type ThresholdDraft,
} from '@/features/home-readings/format';
import type {
  ThresholdKey,
  ThresholdsView,
} from '@/features/home-readings/types';
import { isApiError } from '@/lib/api';
import { clinicDayLabel } from '@/pages/ops/mc-format';
import styles from './HomeReadings.module.css';

/**
 * Per-patient alert thresholds (RL-1141, RL-997 `reading-thresholds`):
 * empty = the clinic default; the same checks as the API before saving;
 * the save sends `version` (0 the first time) and a 409 VERSION_CONFLICT
 * reloads the latest values.
 */
export function ReadingThresholdsDrawer({
  patientId,
  onClose,
}: {
  patientId: string;
  onClose: () => void;
}) {
  const q = useReadingThresholds(patientId);
  // Survives the remount that a reload after VERSION_CONFLICT causes.
  const [conflict, setConflict] = useState<string | null>(null);
  if (q.isLoading || q.isError || !q.data)
    return (
      <Dialog
        open
        title="Alert thresholds"
        onClose={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        {q.isError ? (
          <ErrorState
            error={q.error}
            title="Could not load the thresholds"
            onRetry={() => void q.refetch()}
          />
        ) : (
          <LoadingState label="Loading the thresholds" />
        )}
      </Dialog>
    );
  // Remount the form on a new version so a reload replaces the draft.
  return (
    <ThresholdsForm
      key={q.data.version}
      view={q.data}
      conflict={conflict}
      onConflict={setConflict}
      onClose={onClose}
    />
  );
}

function ThresholdsForm({
  view,
  conflict,
  onConflict,
  onClose,
}: {
  view: ThresholdsView;
  /** requestId of the save that met a newer version, if any. */
  conflict: string | null;
  onConflict: (requestId: string) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const save = useSaveThresholds(view.patient.id);
  const [draft, setDraft] = useState<ThresholdDraft>(() => draftOf(view.own));
  const [shown, setShown] = useState(false);
  const [serverErrors, setServerErrors] = useState<
    Partial<Record<ThresholdKey, string>>
  >({});
  const [failure, setFailure] = useState<{
    title: string;
    requestId?: string;
  } | null>(null);
  const { values, errors } = validateThresholds(draft, view.defaults);
  const invalid = Object.keys(errors).length > 0;

  async function submit() {
    setShown(true);
    setFailure(null);
    setServerErrors({});
    if (invalid) return;
    try {
      await save.mutateAsync({ version: view.version, ...values });
      toast.show({ tone: 'success', title: 'Thresholds saved' });
      onClose();
    } catch (err) {
      if (!isApiError(err))
        return setFailure({ title: 'Could not reach the server. Try again.' });
      const fields: Partial<Record<ThresholdKey, string>> = {};
      for (const f of THRESHOLD_FIELDS) {
        const m = err.fieldError(f.key);
        if (m) fields[f.key] = m;
      }
      if (err.code === 'VERSION_CONFLICT')
        return onConflict(err.requestId ?? '');
      setServerErrors(fields);
      setFailure({ title: err.message, requestId: err.requestId });
    }
  }

  return (
    <Dialog
      open
      title={`Alert thresholds · ${view.patient.fullName}`}
      onClose={onClose}
      dismissible={!save.isPending}
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => void submit()}
          >
            Save thresholds
          </Button>
        </>
      }
    >
      <form
        className={styles.drawer}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className={styles.muted}>
          A home reading outside these values alerts the care team. Leave a
          value empty to use the clinic default. Applies to readings saved from
          now on.
        </p>
        <div className={styles.thresholdGrid}>
          {THRESHOLD_FIELDS.map((f) => (
            <Field
              key={f.key}
              label={`${f.label} (${f.unit})`}
              hint={`${f.help} ${f.min} to ${f.max}; default ${view.defaults[f.key]}.`}
              error={(shown ? errors[f.key] : undefined) ?? serverErrors[f.key]}
            >
              <Input
                inputMode={f.integer ? 'numeric' : 'decimal'}
                value={draft[f.key]}
                placeholder={String(view.defaults[f.key])}
                onChange={(e) =>
                  setDraft({ ...draft, [f.key]: e.target.value })
                }
              />
            </Field>
          ))}
        </div>
        {conflict !== null && !failure && (
          <div className={styles.failure} role="alert">
            <strong>
              Someone else changed these thresholds. The latest values are
              loaded: check them and save again.
            </strong>
            {conflict && (
              <span className={styles.muted}>Reference: {conflict}</span>
            )}
          </div>
        )}
        {failure && (
          <div className={styles.failure} role="alert">
            <strong>{failure.title}</strong>
            {failure.requestId && (
              <span className={styles.muted}>
                Reference: {failure.requestId}
              </span>
            )}
          </div>
        )}
        {view.updatedAt && (
          <p className={styles.muted}>
            Last changed {clinicDayLabel(view.updatedAt)}.
          </p>
        )}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
